import {
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { CircleIcon, PermissionsIcon } from '../icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { SearchPanel } from './SearchPanel'
import { strings } from '../strings'
import {
  AutoStatsRover,
  UserConnection,
  UserItem,
  useAutoStatsRovers,
  useUserGroupsList,
  useUsersList,
} from '../api'
import { GroupForm } from '../GroupForm'
import { useToken } from '../App'
import Card from 'common/Card'
import RoomIcon from '@material-ui/icons/Room'
import BarChartIcon from '@material-ui/icons/BarChart'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { useMapContext } from 'Map/context/MapProvider'
import { RoverReportDialog } from './Analytics/RoverReportDialog'
import { tehranTodayKey } from './Analytics/analyticsShared'

const RAYMAND_AGENTS = [
  'NTRIP RayGnssCore',
  'NTRIP RaymandNTRIPClient',
  'RAYMAND ntclient',
]

type AgentFilter = 'raymand' | 'non-raymand' | null

function isRaymandAgent(agent: string | undefined): boolean {
  if (!agent) return false
  return RAYMAND_AGENTS.some((a) => agent.includes(a))
}

function hasRaymandConnection(connections: UserConnection[] | null): boolean {
  if (!connections) return false
  return connections.some((c) => isRaymandAgent(c.user_agent))
}

function hasNonRaymandConnection(
  connections: UserConnection[] | null,
): boolean {
  if (!connections) return false
  return connections.some((c) => c.user_agent && !isRaymandAgent(c.user_agent))
}

interface RoverWindow {
  from: string
  to: string
}

function makeRoverWindow(end: Date, days: number): RoverWindow {
  const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000)
  return { from: start.toISOString(), to: end.toISOString() }
}

function RoverWindowFetcher({
  window,
  onLoaded,
}: {
  window: RoverWindow
  onLoaded: (key: string, rovers: AutoStatsRover[]) => void
}) {
  const { rovers } = useAutoStatsRovers(window.from, window.to)
  const key = window.from
  useEffect(() => {
    if (rovers) onLoaded(key, rovers)
  }, [key, rovers, onLoaded])
  return null
}

function buildReportRover(x: UserItem): AutoStatsRover {
  return {
    userId: x.old_id,
    username: x.username || '',
    displayName: x.display_name || '',
    phoneNumber: x.phone_number || '',
    status: 'offline',
    isRaymand: false,
    userAgent: '',
    userAgents: [],
    totalConnections: 0,
    basesCreated: 0,
    basesConnectedTo: 0,
    totalDurationSeconds: 0,
    maxSessionDurationSeconds: 0,
    lastActiveUnix: 0,
  }
}

export function Users() {
  const classes = useStyles()

  const { flyToClientPosition } = useMapContext()

  const history = useHistory()
  const [query, setQuery] = useState('')
  const [group, setGroup] = useState<number | null>(null)
  const [agentFilter, setAgentFilter] = useState<AgentFilter>(null)
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null)
  const [connectionsSort, setConnectionsSort] = useState<'desc' | 'asc' | null>(
    null,
  )
  const { usersList, loading, error } = useUsersList('', group)
  const [roverWindows, setRoverWindows] = useState<RoverWindow[]>(() => [
    makeRoverWindow(new Date(), 30),
  ])
  const [roverWindowData, setRoverWindowData] = useState<
    Record<string, AutoStatsRover[]>
  >({})
  const handleRoverWindowData = useCallback(
    (key: string, data: AutoStatsRover[]) => {
      setRoverWindowData((prev) =>
        prev[key] === data ? prev : { ...prev, [key]: data },
      )
    },
    [],
  )
  const isLoadingMoreRovers = roverWindows.some((w) => !roverWindowData[w.from])
  const loadMoreRovers = useCallback(() => {
    setRoverWindows((prev) => {
      const oldest = prev[prev.length - 1]
      const olderTo = new Date(oldest.from)
      return [...prev, makeRoverWindow(olderTo, 30)]
    })
  }, [])
  const allRovers = useMemo<AutoStatsRover[]>(() => {
    const map = new Map<number, AutoStatsRover>()
    for (const w of roverWindows) {
      const data = roverWindowData[w.from]
      if (!data) continue
      for (const r of data) {
        if (!map.has(r.userId)) map.set(r.userId, r)
      }
    }
    return Array.from(map.values())
  }, [roverWindows, roverWindowData])
  const { userGroupsList } = useUserGroupsList()
  const { ut } = useToken()
  const canSeeUserAgent = ut === 'admin' || ut === 'operator'
  const isAdmin = ut === 'admin'

  const todayKey = tehranTodayKey()
  const [reportRover, setReportRover] = useState<AutoStatsRover | null>(null)

  const mergedList = useMemo<
    Array<UserItem & { isRover?: boolean }> | undefined
  >(() => {
    if (!usersList) return undefined
    const known = new Set(usersList.map((u) => u.old_id))
    const roverItems: Array<UserItem & { isRover: true }> = allRovers
      .filter((r) => !known.has(r.userId))
      .map((r) => ({
        id: `rover-${r.userId}`,
        old_id: r.userId,
        username: r.username,
        display_name: r.displayName,
        groupName: null,
        phone_number: r.phoneNumber,
        connected: false,
        connections: null,
        isRover: true,
      }))
    return [...usersList, ...roverItems]
  }, [usersList, allRovers])

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 80 &&
        !isLoadingMoreRovers
      ) {
        loadMoreRovers()
      }
    },
    [isLoadingMoreRovers, loadMoreRovers],
  )
  // const newUser = useNewUser()

  let dataTable

  if (loading)
    dataTable = (
      <Row>
        <CircularProgress size={20} />
      </Row>
    )
  else if (error)
    dataTable = (
      <Row>
        <Typography color="error">{error.message}</Typography>
      </Row>
    )
  else if (mergedList)
    dataTable = mergedList
      .filter((x) => {
        if (!agentFilter) return true
        if (agentFilter === 'raymand')
          return hasRaymandConnection(x.connections)
        return hasNonRaymandConnection(x.connections)
      })
      .filter((x) => {
        if (!query) return true
        const q = query.toLowerCase()
        if (
          (x.display_name || '').toLowerCase().includes(q) ||
          (x.username || '').toLowerCase().includes(q) ||
          (x.phone_number || '').toLowerCase().includes(q)
        ) {
          return true
        }
        return (x.connections || []).some((c) =>
          (c.mountpoint || '').toLowerCase().includes(q),
        )
      })
      .sort((a, b) => {
        const aRover = a.isRover ? 1 : 0
        const bRover = b.isRover ? 1 : 0
        if (aRover !== bRover) return aRover - bRover
        if (connectionsSort) {
          const ca = a.connections?.length || 0
          const cb = b.connections?.length || 0
          if (ca !== cb) return connectionsSort === 'desc' ? cb - ca : ca - cb
        }
        if (a.connected && b.connected) return 0
        if (a.connected) return -1
        return 1
      })
      .flatMap((x) => {
        const connections = x.connections || []
        const count = connections.length
        const isExpanded = expandedUserId === x.id && count > 0
        const rows = [
          <tr key={x.id}>
            <td>
              <CircleIcon
                style={{ color: x.connected ? 'green' : 'lightgray' }}
              />
            </td>
            <td className={classes.nameCell}>
              <Tooltip title={x.display_name || x.username || ''} arrow>
                <Button
                  onClick={() => history.push(`user/${x.old_id}/profile`)}
                  classes={{ label: classes.nameLabel }}
                  style={{
                    textDecoration: x.display_name ? 'unset' : 'underline',
                    textTransform: 'none',
                  }}
                >
                  {x.display_name ? x.display_name : x.username}
                </Button>
              </Tooltip>
            </td>
            <td className={classes.usernameCell}>
              <Tooltip title={x.username || ''} arrow>
                <span className={classes.ellipsis}>{x.username}</span>
              </Tooltip>
            </td>
            <td className={classes.phoneCell}>
              <Tooltip title={x.phone_number || ''} arrow>
                <span className={classes.ellipsis}>{x.phone_number}</span>
              </Tooltip>
            </td>
            <td className={classes.connectionsCell}>
              {count > 0 ? (
                <Button
                  size="small"
                  onClick={() => setExpandedUserId(isExpanded ? null : x.id)}
                  className={classes.connectionsButton}
                >
                  <span className={classes.connectionsCount}>{count}</span>
                  {isExpanded ? (
                    <KeyboardArrowUpIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowDownIcon fontSize="small" />
                  )}
                </Button>
              ) : null}
            </td>
            <td className={classes.activityReport}>
              {isAdmin && (
                <Tooltip title={strings.activityReport} arrow>
                  <IconButton
                    size="small"
                    onClick={() => setReportRover(buildReportRover(x))}
                  >
                    <BarChartIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </td>
            <td className={classes.actionCell}>
              {/* Mobile */}
              <IconButton
                size="small"
                onClick={() => {
                  history.push('/')
                  setTimeout(() => {
                    flyToClientPosition(x.old_id)
                  }, 250)
                }}
                disabled={!x.connected}
                className={classes.mobileLocation}
              >
                <RoomIcon fontSize="small" />
              </IconButton>
              {/* Desktop */}
              <IconButton
                size="small"
                onClick={() => flyToClientPosition(x.old_id)}
                disabled={!x.connected}
                className={classes.desktopLocation}
              >
                <RoomIcon fontSize="small" />
              </IconButton>
            </td>
          </tr>,
        ]
        if (isExpanded) {
          rows.push(
            <tr key={`${x.id}-expanded`} className={classes.expandedRow}>
              <td />
              <td colSpan={6} className={classes.expandedCell}>
                {connections.map((c, i) => (
                  <div key={i} className={classes.connectionLine}>
                    {canSeeUserAgent && (
                      <>
                        <Tooltip title={c.user_agent || ''} arrow>
                          <span
                            className={classes.agentText}
                            style={{ direction: 'ltr', textAlign: 'left' }}
                          >
                            {c.user_agent || '—'}
                          </span>
                        </Tooltip>
                        <span className={classes.connectionArrow}>→</span>
                      </>
                    )}
                    <span className={classes.mountpointText}>
                      {c.mountpoint || '—'}
                    </span>
                  </div>
                ))}
              </td>
            </tr>,
          )
        }
        return rows
      })

  return (
    <>
      <Card title={strings.users}>
        <SearchPanel
          onChanged={(query, group) => {
            setQuery(query)
            setGroup(group)
          }}
          placeholder="جستجو در نام، نام کاربری، شماره موبایل"
          groups={userGroupsList}
          // showNew={!usersList?.length && query.match(/^[a-z0-9_]{4,20}$/) !== null}
          // onNew={() => {
          //   newUser(query, group)
          //     .then((data) => console.log(data))
          //     .catch((err) => console.log(err.message))
          // }}
        />
        {canSeeUserAgent && (
          <Autocomplete
            clearOnBlur
            options={[
              { id: 'raymand', name: strings.raymandAgent },
              { id: 'non-raymand', name: strings.nonRaymandAgent },
            ]}
            getOptionLabel={(x) => x.name}
            getOptionSelected={(o, v) => o.id === v.id}
            value={
              agentFilter === 'raymand'
                ? { id: 'raymand', name: strings.raymandAgent }
                : agentFilter === 'non-raymand'
                  ? { id: 'non-raymand', name: strings.nonRaymandAgent }
                  : null
            }
            onChange={(_e, x) =>
              setAgentFilter(x ? (x.id as AgentFilter) : null)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoComplete="off"
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  style: { unicodeBidi: 'normal' },
                }}
                InputProps={{
                  ...params.InputProps,
                  placeholder: strings.userAgentFilter,
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PermissionsIcon htmlColor="#333" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        )}
        {/* <th>{strings.nationalCode}</th> */}

        <GroupForm groupId={group} groupType="user" />
      </Card>
      {roverWindows.map((w) => (
        <RoverWindowFetcher
          key={w.from}
          window={w}
          onLoaded={handleRoverWindowData}
        />
      ))}
      <Paper className={classes.result} onScroll={handleScroll}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ width: 33, borderBottom: 'none' }}>&nbsp;</th>
              <th>{strings.name}</th>
              <th className={classes.usernameCell}>{strings.username}</th>
              <th className={classes.phoneCell}>{strings.phoneNumber}</th>
              <th className={classes.connectionsCell}>
                <span
                  className={classes.sortableHeader}
                  onClick={() =>
                    setConnectionsSort((prev) =>
                      prev === null ? 'desc' : prev === 'desc' ? 'asc' : null,
                    )
                  }
                >
                  {strings.connections}
                  {connectionsSort === 'desc' && (
                    <KeyboardArrowDownIcon
                      fontSize="small"
                      className={classes.sortIcon}
                    />
                  )}
                  {connectionsSort === 'asc' && (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      className={classes.sortIcon}
                    />
                  )}
                </span>
              </th>
              <th className={classes.activityReport}>
                {isAdmin ? strings.activityReport : null}
              </th>
              <th className={classes.actionCell} />
            </tr>
          </thead>
          <tbody>
            {dataTable}
            {isLoadingMoreRovers && mergedList && (
              <Row>
                <CircularProgress size={16} />
              </Row>
            )}
          </tbody>
        </table>
      </Paper>
      <RoverReportDialog
        open={reportRover !== null}
        onClose={() => setReportRover(null)}
        rover={reportRover}
        from={todayKey}
        to={todayKey}
        showDatePicker
      />
    </>
  )
}

function Row({ children }: any) {
  return (
    <tr>
      <td colSpan={7} style={{ textAlign: 'center' }}>
        {children}
      </td>
    </tr>
  )
}

const useStyles = makeStyles((theme) => ({
  search: {
    flex: '0 1 60px',
    width: '100%',
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: '#fff',
    '& .MuiButtonBase-root': {
      padding: theme.spacing(0.75),
    },
    '& .tags': {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      '& .MuiButtonBase-root': {
        borderRadius: theme.spacing(2),
        marginRight: theme.spacing(1),
        fontSize: '0.6rem',
        fontWeight: 'normal',
        padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
      },
    },
  },
  result: {
    flex: '1 1 auto',
    width: '100%',
    marginTop: theme.spacing(1),
    backgroundColor: '#fff',
    padding: theme.spacing(2),
    overflowX: 'auto',
    overflowY: 'auto',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  table: {
    width: '100%',
    minWidth: 'max-content',
    borderCollapse: 'collapse',
    tableLayout: 'auto',
    '& tr > td': {
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(0.5),
      cursor: 'pointer',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    },
    // '& tr:hover td': {
    //   backgroundColor: '#f8f8f8',
    // },
    '& th': {
      borderBottom: '1px solid lightgray',
      fontWeight: 'normal',
      textAlign: 'left',
      paddingBottom: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      whiteSpace: 'nowrap',
    },
    '& tr th:last-child, & tr td:last-child': {
      textAlign: 'right',
      paddingRight: 0,
    },
  },
  ellipsis: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
  },
  nameCell: {
    whiteSpace: 'nowrap',
  },
  nameLabel: {
    display: 'block',
    whiteSpace: 'nowrap',
  },
  usernameCell: {
    whiteSpace: 'nowrap',
  },
  phoneCell: {
    whiteSpace: 'nowrap',
  },
  actionCell: {
    width: 40,
    minWidth: 40,
    whiteSpace: 'nowrap',
  },
  connectionsCell: {
    whiteSpace: 'nowrap',
    minWidth: 60,
  },
  activityReport: {
    width: 40,
    minWidth: 40,
    whiteSpace: 'nowrap',
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  sortIcon: {
    fontSize: '1rem',
  },
  connectionsButton: {
    minWidth: 0,
    padding: `${theme.spacing(0.25)}px ${theme.spacing(0.75)}px`,
    textTransform: 'none',
  },
  connectionsCount: {
    display: 'inline-block',
    minWidth: 20,
    textAlign: 'center',
    fontWeight: 500,
    marginRight: theme.spacing(0.25),
  },
  expandedRow: {
    backgroundColor: '#fafafa',
  },
  expandedCell: {
    padding: theme.spacing(1),
    '&&': {
      cursor: 'default',
      whiteSpace: 'normal',
    },
  },
  connectionLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: `${theme.spacing(0.25)}px 0`,
    fontSize: '0.85rem',
  },
  agentText: {
    display: 'inline-block',
    color: '#555',
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    unicodeBidi: 'bidi-override',
    verticalAlign: 'middle',
  },
  connectionArrow: {
    color: '#999',
  },
  mountpointText: {
    fontWeight: 500,
  },
  mobileLocation: {
    padding: 1,
    '& svg': {
      marginTop: 0,
    },
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  desktopLocation: {
    padding: 1,
    '& svg': {
      marginTop: 0,
    },
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'unset',
    },
  },
}))
